import React, { useEffect, useState } from "react";
import { X, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * P4 · Buyer Intent Sheet
 * Bottom sheet that turns a Live World Need (discovery) into a structured
 * transaction Intent (execution). Prefills fields from the tapped need so
 * posting takes one confirm-tap.
 *
 * POST /api/transactions/intents  — creates a fresh Intent in BID state.
 */
export default function PostAsNeedSheet({ open, need, loc, onClose, onCreated }) {
  const [product, setProduct]   = useState("");
  const [category, setCategory] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit]         = useState("piece");
  const [targetPrice, setTargetPrice] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [notes, setNotes]       = useState("");
  const [delivery, setDelivery] = useState(false);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (!need) return;
    // Prefill from the discovery card. Buyer can override anything.
    setProduct(need.title || need.product || "");
    setCategory((need.category || "").toLowerCase());
    setQuantity(Number(need.quantity) > 0 ? Number(need.quantity) : 1);
    setUnit(need.unit || "piece");
    const price = need.bid ?? need.price_max ?? need.target_price ?? "";
    setTargetPrice(price === null || price === undefined ? "" : String(price));
    setCurrency(need.currency || "INR");
    setNotes("");
    setDelivery(false);
  }, [need]);

  if (!open) return null;

  const submit = async () => {
    if (!product.trim()) { toast.error("Product is required"); return; }
    if (!(Number(quantity) > 0)) { toast.error("Quantity must be > 0"); return; }
    setSaving(true);
    try {
      const body = {
        product:  product.trim(),
        category: category || null,
        quantity: Number(quantity),
        unit:     unit || "piece",
        currency,
        target_price:      targetPrice === "" ? null : Number(targetPrice),
        delivery_required: !!delivery,
        notes,
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
      };
      const { data } = await api.post("/transactions/intents", body);
      toast.success("Need posted — providers can now offer.");
      onCreated?.(data?.intent);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to post need";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="post-as-need-sheet"
      className="fixed inset-0 z-[80] bg-black/50 flex items-end"
      onClick={onClose}
    >
      <div
        className="w-full bg-white rounded-t-3xl p-5 space-y-3 max-h-[85vh] overflow-y-auto mx-auto max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase font-black text-red-600 tracking-wide">Post as Need</div>
            <h3 className="text-lg font-black text-stone-900">Turn this into a request for offers</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-stone-100 grid place-items-center"
            data-testid="post-as-need-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-stone-500 leading-relaxed">
          Posting a Need is <b>not</b> a payment. Providers will see it and can send offers.
          You can accept, counter, or ignore each one.
        </p>

        <div className="space-y-2">
          <label className="block text-[11px] font-bold text-stone-600 uppercase">Product</label>
          <input
            data-testid="pan-product"
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder="e.g. 24k Gold Coin, TMT Steel Bar"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-bold text-stone-600 uppercase">Quantity</label>
            <input
              data-testid="pan-qty"
              type="number" min="0" step="any"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-stone-600 uppercase">Unit</label>
            <input
              data-testid="pan-unit"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="kg, piece, gram…"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-bold text-stone-600 uppercase">Target Price</label>
            <input
              data-testid="pan-price"
              type="number" min="0" step="any"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-stone-600 uppercase">Currency</label>
            <select
              data-testid="pan-currency"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm bg-white"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {["INR", "USD", "EUR", "GBP", "AED", "WFC"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-bold text-stone-600 uppercase">Category</label>
          <input
            data-testid="pan-category"
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value.toLowerCase())}
            placeholder="commodity, gold, vehicle…"
          />
        </div>

        <div>
          <label className="block text-[11px] font-bold text-stone-600 uppercase">Notes</label>
          <textarea
            data-testid="pan-notes"
            rows={2}
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional context for providers"
          />
        </div>

        <label className="flex items-center gap-2 text-xs font-semibold text-stone-700">
          <input
            data-testid="pan-delivery"
            type="checkbox" checked={delivery}
            onChange={(e) => setDelivery(e.target.checked)}
          />
          Delivery required
        </label>

        <button
          data-testid="pan-submit"
          disabled={saving}
          onClick={submit}
          className="w-full mt-2 px-4 py-3 rounded-full bg-[#E05E36] text-white font-black text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {saving ? "Posting…" : "Post as Need"}
        </button>
      </div>
    </div>
  );
}
